import { io } from 'socket.io-client';
import { updateProfession, deleteProfession } from '../redux/slices/professionsSlice';
import { updateDepartment, deleteDepartment } from '../redux/slices/departmentsSlice';
import { updateStatus, deleteStatus } from '../redux/slices/statusesSlice';
import { updatePriority, deletePriority } from '../redux/slices/prioritiesSlice';
import { updateLanguage, deleteLanguage } from '../redux/slices/languagesSlice';
import { updateTool, deleteTool } from '../redux/slices/toolsSlice';

class LibraryEventsService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.isInitialized = false;
    this.dispatch = null;
    this.subscribers = {
      profession: [],
      department: [],
      status: [],
      priority: [],
      language: [],
      tool: []
    };
    this.connectionAttempts = 0;
    this.maxConnectionAttempts = 5;
  }

  init(dispatch) {
    if (this.isInitialized) return;
    
    this.dispatch = dispatch;
    this.connect();
    this.isInitialized = true;

    // Автоматическое переподключение в случае разрыва
    window.addEventListener('online', () => {
      if (!this.isConnected) {
        console.log('Интернет-соединение восстановлено, переподключение к событиям...');
        this.connect();
      }
    });
  }

  connect() {
    try {
      if (this.connectionAttempts >= this.maxConnectionAttempts) {
        console.log(`Достигнуто максимальное количество попыток подключения (${this.maxConnectionAttempts}). Остановка попыток.`);
        return;
      }

      this.connectionAttempts++;
      console.log(`Попытка подключения к сервису событий ${this.connectionAttempts}/${this.maxConnectionAttempts}`);

      // Получаем URL из переменных окружения или используем значение по умолчанию
      const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;
      
      console.log('Подключение к Socket.IO по адресу:', SOCKET_URL);
      console.log('🌍 Окружение:', process.env.NODE_ENV || 'development');
      
      // Если уже есть активное соединение, закрываем его
      if (this.socket) {
        this.socket.disconnect();
        this.socket = null;
      }

      this.socket = io(SOCKET_URL, {
        path: '/socket.io',
        transports: ['websocket', 'polling'], // Сначала WebSocket, потом polling
        reconnection: true,
        reconnectionAttempts: 10, // Увеличиваем количество попыток
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 30000, // Увеличиваем таймаут
        forceNew: false,
        // Дополнительные настройки для продакшена
        upgrade: true,
        rememberUpgrade: true,
        autoConnect: true,
        // Добавляем обработку ошибок транспорта
        transportOptions: {
          polling: {
            extraHeaders: {
              'X-Client-Domain': window.location.origin
            }
          }
        }
      });

      this.socket.on('connect', () => {
        console.log('Подключение к сервису событий библиотек установлено');
        this.isConnected = true;
        this.connectionAttempts = 0; // Сбрасываем счетчик попыток
        
        // Подписываемся на события библиотек
        this.socket.on('library:profession:created', this.handleProfessionCreated.bind(this));
        this.socket.on('library:profession:updated', this.handleProfessionUpdated.bind(this));
        this.socket.on('library:profession:deleted', this.handleProfessionDeleted.bind(this));
        
        this.socket.on('library:department:created', this.handleDepartmentCreated.bind(this));
        this.socket.on('library:department:updated', this.handleDepartmentUpdated.bind(this));
        this.socket.on('library:department:deleted', this.handleDepartmentDeleted.bind(this));
        
        this.socket.on('library:status:created', this.handleStatusCreated.bind(this));
        this.socket.on('library:status:updated', this.handleStatusUpdated.bind(this));
        this.socket.on('library:status:deleted', this.handleStatusDeleted.bind(this));
        
        this.socket.on('library:priority:created', this.handlePriorityCreated.bind(this));
        this.socket.on('library:priority:updated', this.handlePriorityUpdated.bind(this));
        this.socket.on('library:priority:deleted', this.handlePriorityDeleted.bind(this));
        
        // Подписываемся на события языков
        this.socket.on('library:language:created', this.handleLanguageCreated.bind(this));
        this.socket.on('library:language:updated', this.handleLanguageUpdated.bind(this));
        this.socket.on('library:language:deleted', this.handleLanguageDeleted.bind(this));

        // Подписываемся на события инструментов
        this.socket.on('library:tool:created', this.handleToolCreated.bind(this));
        this.socket.on('library:tool:updated', this.handleToolUpdated.bind(this));
        this.socket.on('library:tool:deleted', this.handleToolDeleted.bind(this));
      });

      this.socket.on('connect_error', (error) => {
        console.error('Ошибка подключения к Socket.IO:', error);
        this.isConnected = false;
        
        // Пытаемся переподключиться через 5 секунд
        setTimeout(() => {
          if (!this.isConnected) {
            this.connect();
          }
        }, 5000);
      });

      this.socket.on('disconnect', () => {
        console.log('Соединение с сервисом событий библиотек разорвано');
        this.isConnected = false;
        
        // Пытаемся переподключиться через 5 секунд
        setTimeout(() => {
          if (!this.isConnected) {
            this.connect();
          }
        }, 5000);
      });

      this.socket.on('error', (error) => {
        console.error('Ошибка в соединении с сервисом событий:', error);
        this.isConnected = false;
      });
      
    } catch (error) {
      console.error('Ошибка при инициализации сервиса событий библиотек:', error);
      
      // Пытаемся переподключиться через 5 секунд
      setTimeout(() => {
        if (!this.isConnected) {
          this.connect();
        }
      }, 5000);
    }
  }

  // Обработчики событий профессий
  handleProfessionCreated(data) {
    console.log('Получено событие создания профессии:', data);
    try {
      if (this.dispatch && data && data.data) {
        this.dispatch(updateProfession(data.data));
      }
      this.notify('profession', 'create', data.data || data);
    } catch (error) {
      console.error('Ошибка при обработке события создания профессии:', error);
    }
  }

  handleProfessionUpdated(data) {
    console.log('Получено событие обновления профессии:', data);
    try {
      if (this.dispatch && data && data.data) {
        this.dispatch(updateProfession(data.data));
      }
      this.notify('profession', 'update', data.data || data);
    } catch (error) {
      console.error('Ошибка при обработке события обновления профессии:', error);
    }
  }

  handleProfessionDeleted(data) {
    console.log('Получено событие удаления профессии:', data);
    try {
      // Извлекаем id из разных возможных форматов данных
      let professionId = null;
      
      if (data && data.id) {
        // Формат { id: 'xxx' }
        professionId = data.id;
      } else if (data && data.data && data.data.id) {
        // Формат { data: { id: 'xxx' } }
        professionId = data.data.id;
      } else if (typeof data === 'string') {
        // Формат 'xxx' (строка с ID)
        professionId = data;
      } else if (data && data._id) {
        // Формат { _id: 'xxx' }
        professionId = data._id;
      } else if (typeof data === 'object') {
        // Проверяем, является ли сам объект ID (как в формате {id: '29'})
        const possibleId = Object.values(data)[0];
        if (typeof possibleId === 'string' && /^\d+$/.test(possibleId)) {
          professionId = possibleId;
        }
      }
      
      console.log('Извлеченный ID профессии для удаления:', professionId);
      
      if (professionId && this.dispatch) {
        console.log('Вызов dispatch для удаления профессии с ID:', professionId);
        this.dispatch(deleteProfession(professionId));
        this.notify('profession', 'delete', { id: professionId });
      } else {
        console.error('Не удалось извлечь ID профессии из данных события:', data);
      }
    } catch (error) {
      console.error('Ошибка при обработке события удаления профессии:', error);
    }
  }

  // Обработчики событий департаментов
  handleDepartmentCreated(data) {
    console.log('Получено событие создания департамента:', data);
    try {
      if (this.dispatch && data && data.data) {
        this.dispatch(updateDepartment(data.data));
      }
      this.notify('department', 'create', data.data || data);
    } catch (error) {
      console.error('Ошибка при обработке события создания департамента:', error);
    }
  }

  handleDepartmentUpdated(data) {
    console.log('Получено событие обновления департамента:', data);
    try {
      if (this.dispatch && data && data.data) {
        this.dispatch(updateDepartment(data.data));
      }
      this.notify('department', 'update', data.data || data);
    } catch (error) {
      console.error('Ошибка при обработке события обновления департамента:', error);
    }
  }

  handleDepartmentDeleted(data) {
    console.log('Получено событие удаления департамента:', data);
    try {
      if (this.dispatch && data && data.id) {
        this.dispatch(deleteDepartment(data.id));
      }
      this.notify('department', 'delete', { id: data.id });
    } catch (error) {
      console.error('Ошибка при обработке события удаления департамента:', error);
    }
  }

  // Обработчики событий статусов
  handleStatusCreated(data) {
    console.log('Получено событие создания статуса:', data);
    try {
      if (this.dispatch && data && data.data) {
        this.dispatch(updateStatus(data.data));
      }
      this.notify('status', 'create', data.data || data);
    } catch (error) {
      console.error('Ошибка при обработке события создания статуса:', error);
    }
  }

  handleStatusUpdated(data) {
    console.log('Получено событие обновления статуса:', data);
    try {
      if (this.dispatch && data && data.data) {
        this.dispatch(updateStatus(data.data));
      }
      this.notify('status', 'update', data.data || data);
    } catch (error) {
      console.error('Ошибка при обработке события обновления статуса:', error);
    }
  }

  handleStatusDeleted(data) {
    console.log('Получено событие удаления статуса:', data);
    try {
      if (this.dispatch && data && data.id) {
        this.dispatch(deleteStatus(data.id));
      }
      this.notify('status', 'delete', { id: data.id });
    } catch (error) {
      console.error('Ошибка при обработке события удаления статуса:', error);
    }
  }

  // Обработчики событий приоритетов
  handlePriorityCreated(data) {
    console.log('Получено событие создания приоритета:', data);
    try {
      if (this.dispatch && data && data.data) {
        this.dispatch(updatePriority(data.data));
      }
      this.notify('priority', 'create', data.data || data);
    } catch (error) {
      console.error('Ошибка при обработке события создания приоритета:', error);
    }
  }

  handlePriorityUpdated(data) {
    console.log('Получено событие обновления приоритета:', data);
    try {
      if (this.dispatch && data && data.data) {
        this.dispatch(updatePriority(data.data));
      }
      this.notify('priority', 'update', data.data || data);
    } catch (error) {
      console.error('Ошибка при обработке события обновления приоритета:', error);
    }
  }

  handlePriorityDeleted(data) {
    console.log('Получено событие удаления приоритета:', data);
    try {
      if (this.dispatch && data && data.id) {
        this.dispatch(deletePriority(data.id));
      }
      this.notify('priority', 'delete', { id: data.id });
    } catch (error) {
      console.error('Ошибка при обработке события удаления приоритета:', error);
    }
  }

  // Обработчики событий языков
  handleLanguageCreated(data) {
    console.log('Получено событие создания языка:', data);
    try {
      if (this.dispatch && data && data.data) {
        this.dispatch(updateLanguage(data.data));
      }
      this.notify('language', 'create', data.data || data);
    } catch (error) {
      console.error('Ошибка при обработке события создания языка:', error);
    }
  }

  handleLanguageUpdated(data) {
    console.log('Получено событие обновления языка:', data);
    try {
      if (this.dispatch && data && data.data) {
        this.dispatch(updateLanguage(data.data));
      }
      this.notify('language', 'update', data.data || data);
    } catch (error) {
      console.error('Ошибка при обработке события обновления языка:', error);
    }
  }

  handleLanguageDeleted(data) {
    console.log('Получено событие удаления языка:', data);
    try {
      // Извлекаем id из разных возможных форматов данных
      let languageId = null;

      if (data && data.id) {
        // Формат { id: 'xxx' }
        languageId = data.id;
      } else if (data && data.data && data.data.id) {
        // Формат { data: { id: 'xxx' } }
        languageId = data.data.id;
      } else if (typeof data === 'string') {
        // Формат 'xxx' (строка с ID)
        languageId = data;
      } else if (data && data._id) {
        // Формат { _id: 'xxx' }
        languageId = data._id;
      } else if (typeof data === 'object') {
        // Проверяем, является ли сам объект ID (как в формате {id: '29'})
        const possibleId = Object.values(data)[0];
        if (typeof possibleId === 'string' && /^\d+$/.test(possibleId)) {
          languageId = possibleId;
        }
      }

      console.log('Извлеченный ID языка для удаления:', languageId);

      if (languageId && this.dispatch) {
        console.log('Вызов dispatch для удаления языка с ID:', languageId);
        this.dispatch(deleteLanguage(languageId));
        this.notify('language', 'delete', { id: languageId });
      } else {
        console.error('Не удалось извлечь ID языка из данных события:', data);
      }
    } catch (error) {
      console.error('Ошибка при обработке события удаления языка:', error);
    }
  }

  // Обработчики событий инструментов
  handleToolCreated(data) {
    console.log('Получено событие создания инструмента:', data);
    try {
      if (this.dispatch && data && data.data) {
        this.dispatch(updateTool(data.data));
      }
      this.notify('tool', 'create', data.data || data);
    } catch (error) {
      console.error('Ошибка при обработке события создания инструмента:', error);
    }
  }

  handleToolUpdated(data) {
    console.log('Получено событие обновления инструмента:', data);
    try {
      if (this.dispatch && data && data.data) {
        this.dispatch(updateTool(data.data));
      }
      this.notify('tool', 'update', data.data || data);
    } catch (error) {
      console.error('Ошибка при обработке события обновления инструмента:', error);
    }
  }

  handleToolDeleted(data) {
    console.log('Получено событие удаления инструмента:', data);
    try {
      // Извлекаем id из разных возможных форматов данных
      let toolId = null;

      if (data && data.id) {
        // Формат { id: 'xxx' }
        toolId = data.id;
      } else if (data && data.data && data.data.id) {
        // Формат { data: { id: 'xxx' } }
        toolId = data.data.id;
      } else if (typeof data === 'string') {
        // Формат 'xxx' (строка с ID)
        toolId = data;
      } else if (data && data._id) {
        // Формат { _id: 'xxx' }
        toolId = data._id;
      } else if (typeof data === 'object') {
        // Проверяем, является ли сам объект ID (как в формате {id: '29'})
        const possibleId = Object.values(data)[0];
        if (typeof possibleId === 'string' && /^\d+$/.test(possibleId)) {
          toolId = possibleId;
        }
      }

      console.log('Извлеченный ID инструмента для удаления:', toolId);

      if (toolId && this.dispatch) {
        console.log('Вызов dispatch для удаления инструмента с ID:', toolId);
        this.dispatch(deleteTool(toolId));
        this.notify('tool', 'delete', { id: toolId });
      } else {
        console.error('Не удалось извлечь ID инструмента из данных события:', data);
      }
    } catch (error) {
      console.error('Ошибка при обработке события удаления инструмента:', error);
    }
  }

  // Система подписок для компонентов
  subscribe(entityType, callback) {
    if (!this.subscribers[entityType]) {
      this.subscribers[entityType] = [];
    }
    
    this.subscribers[entityType].push(callback);
    
    // Возвращаем функцию для отписки
    return () => {
      this.subscribers[entityType] = this.subscribers[entityType].filter(cb => cb !== callback);
    };
  }
  
  notify(entityType, action, data) {
    if (!this.subscribers[entityType]) return;
    
    this.subscribers[entityType].forEach(callback => {
      try {
        callback(action, data);
      } catch (error) {
        console.error(`Ошибка при выполнении callback для ${entityType}:${action}:`, error);
      }
    });
  }

  // Закрытие соединения
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.isConnected = false;
    }
  }
}

// Создаем синглтон
const libraryEventsService = new LibraryEventsService();
export default libraryEventsService; 